import { decimalToPercentage } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { TableColumns } from '@/components/table/types';
import { currencyFormatter } from '@/shared/utils/currency';
import { dateFormatter } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { AIFieldType, DocumentRecordSentimentFlag } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import PptxGenJS from 'pptxgenjs';
import { DefaultTheme } from 'styled-components/dist/types';
import { getPublishedUpdatesTableAssetClassValue, getPublishedUpdatesTablePacingValue } from './table/table';

export const UPDATES_SUMMARY_MOCKED_DISCLOSURE_TEXT = `1. Private Capital Historical Manager Performance  
The performance figures presented herein pertain to the four managers in which the firm has made the largest commitments in each asset class. The vehicles shown represent the funds for which the firm has made investments with these managers, excluding any that are still fundraising. The firm will on occasion make direct co-investments, which are not included in this table (information available upon request). The performance figures are provided directly by the fund managers. These resources are available upon request.  

Past performance is no guarantee of future performance, and it is important to realize that actual results may differ from the projections contained in this report. This material is for illustration purposes only and is subject to change. Expected rates of return are shown gross of adviser fees. Adviser fees are subject to change based on the investor’s current assets under management. Adviser fee structure can be found in the advisory firm’s ADV Form 2. A copy of the firm’s ADV 2A is available upon request. Performance is based on valuations provided by the manager. These resources are available upon request.  

Private investing is speculative, involves a high degree of risk, and is designed for sophisticated investors only. Investors could lose some or all of their investment. Certain investment opportunities are not suitable for all investors and may require the signature of certain additional documentation before they may be offered. Individual offerings are subject to capacity. It is important to always compare the information on this report with the statements you receive from the custodian(s) for your account(s).  

2. Private Capital Disclosure in Existing Client Portfolio  
This is a summary of the private investments currently held in your portfolio. The report provides projections based on various assumptions and is therefore hypothetical in nature and not guarantees of investment returns. You should consult your tax and/or legal advisors before implementing any transactions and/or strategies concerning your finances.  

Past performance is no guarantee of future performance, and it is important to realize that actual results may differ from the projections contained in this report. This material is for illustration purposes only and is subject to change. Expected rates of return are shown net of manager and adviser fees. Adviser fees are subject to change based on the investor’s assets under management. Adviser fee structure can be found in the advisory firm’s ADV Form 2. A copy of the firm’s ADV 2A is available upon request. Manager expected returns and yields for private and semi-liquid investments are the return targets reported by the managers in their offering documents. These resources are available upon request.  

Private investing is speculative, involves a high degree of risk, and is designed for sophisticated investors only. Investors could lose some or all of their investment. Certain investment opportunities are not suitable for all investors and may require the signature of certain additional documentation before they may be offered. Individual offerings are subject to capacity.  

3. Private Capital and Public Market Proposal Disclosure  
This proposal was created using client-provided criteria and information and the firm’s current approved and open investment opportunities. The report provides projections based on various assumptions and is therefore hypothetical in nature and not guarantees of investment returns. You should consult your tax and/or legal advisors before implementing any transactions and/or strategies concerning your finances.  

Past performance is no guarantee of future performance, and it is important to realize that actual results may differ from the projections contained in this report. This material is for illustration purposes only and is subject to change. Expected rates of return are shown gross and net of adviser fees. Adviser fees are subject to change based on the investor’s current assets under management. Adviser fee structure can be found in the advisory firm’s ADV Form 2. A copy of the firm’s ADV 2A is available upon request.  

Asset class expected returns are based on capital market assumptions (CMAs) from third-party sources. The CMAs are long-term (typically 10-year) forecasted expected returns. Public assumptions are from Envestnet PMC, and private and semi-liquid are from Fund Evaluation Group. To create more accurate long-term return expectations, the firm uses a rolling 5-year average of the provided CMAs. This minimizes the effect that shorter-term market dislocation could have on CMAs. Public market yields are based on the trailing twelve-month (TTM) yield of our model security for the specific asset class at the time we receive updated CMAs. Exceptions may be made if TTM yields are deemed to give an inaccurate expectation of long-term yields. Asset class expected returns and yields are reviewed and updated annually. A copy of the third-party CMA methodologies as well as the firm’s adjustments are available upon request. Asset class expected returns and yields are updated for 2024.  

Manager expected returns and yields for private and semi-liquid investments are the return targets reported by the managers in their offering documents. The target returns and yields are expected to be achieved over the life of the fund (available within the fund’s offering documents). These resources are available upon request. Private investing is speculative, involves a high degree of risk, and is designed for sophisticated investors only. Investors may lose some or all of their investment. Certain investment opportunities are not suitable for all investors and may require the signature of certain additional documentation before they may be offered. Individual offerings are subject to capacity.  

4. Sample QPR Disclosure  
This report is intended for illustration purposes only. Past performance is not a guarantee of future results. Indices are not available for direct investments. Index performance does not reflect expenses associated with the management of the actual portfolio. Overall account performance is shown net of adviser fees, expense ratios for public investments, and management fees for private investments.  

Private investing is speculative, involves a high degree of risk, and is designed for sophisticated investors only. Investors may lose some or all of their investment. Certain investment opportunities are not suitable for all investors and may require the signature of certain additional documentation before they may be offered. Individual offerings are subject to capacity.  

5. Private Investment Cash Flow Projection Disclosure  
This analysis was prepared using information provided by fund managers to project estimated capital to be called down over the life of each fund and estimated distributions to be made to investors over the life of each fund. It is important to realize that actual capital called and distributions made may significantly differ from these projections. Private investing is speculative and involves a high degree of risk and is designed for sophisticated investors only. This analysis is not meant to provide tax advice. The K-1s provided by each manager will provide the information your tax professional should use for tax planning.  

6. Capital Market Assumption Cash Flow Analysis Disclosure  
Asset class expected returns are based on capital market assumptions (CMAs) from third-party sources. The CMAs are long-term (typically 10-year) forecasted expected returns. Public assumptions are from Envestnet PMC, and private and semi-liquid are from Fund Evaluation Group. To create more accurate long-term return expectations, the firm uses a rolling 5-year average of the provided CMAs. This minimizes the effect that shorter-term market dislocation could have on CMAs. The firm’s maximum fee (90 bps) is deducted from the resulting average. Public market yields are based on the trailing twelve-month (TTM) yield of our model security for the specific asset class at the time we receive updated CMAs. Exceptions may be made if TTM yields are deemed to give an inaccurate expectation of long-term yields. Asset class expected returns and yields are reviewed and updated annually. A copy of the third-party CMA methodologies as well as the firm’s adjustments are available upon request.  

7. General Disclosures  
All reports should be reviewed in conjunction with your fact summary and this Disclosure page. Investors should always compare adviser reports with custodian statements. The firm does not provide legal or tax advice. Nothing included in this presentation is intended to be legal or tax advice. Please consult your legal and tax professionals.  

The firm identifies private investments via a broad array of sources. In some cases, the source of an alternative investment is a Client of the firm. This creates a conflict of interest as the firm has an incentive to recommend private investments that financially benefit Clients over those that do not. While this conflict exists, the firm believes that its policies and procedures mitigate any such conflict. The firm undertakes a thorough due diligence process on all investments, including those that may be introduced to the firm by a Client. Moreover, when making investment recommendations to Clients, the firm makes individualized recommendations based on what it believes to be in the best interest of the Client on whose behalf it is making the recommendation viewed on an overall basis.  

The firm charges ongoing investment management fees (as detailed in your Investment Advisory Agreement) on the total value of all assets invested in our Investment Management service, including the value of your private investments. The firm does not receive any additional compensation for recommending private investments, whether or not sourced by Clients. Fees charged by private investments are separate and apart from our advisory fees. You should refer to the offering documents for a complete description of the fees, investment objectives, risks, and other relevant information associated with investing in private investments.  
`;

export const getPptxImageSize = async ({ url, maxWidth, maxHeight }: { url: string; maxWidth: number; maxHeight: number }) => {
  const img = new window.Image();
  img.crossOrigin = 'anonymous';
  img.src = url;

  await new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve;
  });

  let newWidth = img.naturalWidth;
  let newHeight = img.naturalHeight;

  const widthRatio = maxWidth / newWidth;
  const heightRatio = maxHeight / newHeight;

  const ratio = Math.min(widthRatio, heightRatio);

  if (ratio < 1) {
    newWidth = newWidth * ratio;
    newHeight = newHeight * ratio;
  }

  return {
    widthInInches: newWidth / 96,
    heightInInches: newHeight / 96
  };
};

const getContactInfoString = (firmInfoData: FirmInfoType) => {
  const { address, website, phone } = firmInfoData || {};
  const { streetAddress, city, state, zipCode } = address || {};
  const hasPhone = phone && Object.values(phone || {})?.length > 0;
  const phoneNumber = hasPhone ? formatUserPhoneNumber(phone.countryCode, phone.number) : '';

  const streetAddressString = streetAddress ? `${streetAddress}\n` : '';
  const cityStateZipCodeString = `${city ? ` ${city},` : ''} ${state || ''} ${zipCode || ''}`;
  return `${streetAddressString}${cityStateZipCodeString}\n${phoneNumber || ''}\n${website || ''}`.trim();
};

export const createSlideMaster = (
  pptx: PptxGenJS,
  currentDate: string,
  logoSize: { widthInInches: number; heightInInches: number },
  firmInfoData: FirmInfoType,
  templateStylingData: ITemplateStylingSettings,
  theme: DefaultTheme
) => {
  pptx.defineSlideMaster({
    title: 'SLIDE_MASTER',
    background: { color: '#ffffff' },
    objects: [
      // Header
      { rect: { x: 0, y: 0, w: '100%', h: 0.5, fill: { color: '#efefef' } } },
      {
        text: {
          text: 'Private Capital Summary',
          options: {
            x: 0.5,
            y: 0,
            w: 5.5,
            h: 0.5,
            color: theme.font.strong,
            fontFace: 'Open Sans',
            bold: true,
            fontSize: 12.5
          }
        }
      },
      {
        text: {
          text: currentDate,
          options: {
            x: 8.2,
            y: 0,
            w: 5.5,
            h: 0.5,
            color: theme.font.base,
            fontFace: 'Open Sans',
            italic: true,
            fontSize: 10
          }
        }
      },
      {
        text: {
          text: 'Disclosure: This is a summary of the private investments currently held in your portfolio. The report provides projections based on various assumptions and are therefore hypothetical in nature and not guarantees of investment returns. You should consult your tax and/or legal advisors before implementing any transactions and/or strategies concerning your finances.',
          options: {
            x: 0.5,
            y: 4.65,
            w: 9,
            h: 0.3,
            fontSize: 6,
            fontFace: 'Open Sans',
            color: theme.font.weak
          }
        }
      },
      // Footer
      { rect: { x: 0.5, y: 4.95, w: 9, h: 0.01, fill: { color: theme.border.base } } },
      {
        image: {
          path: templateStylingData?.logo,
          x: 0.5,
          y: 5.1,
          w: logoSize.widthInInches,
          h: logoSize.heightInInches
        }
      },
      {
        text: {
          text: firmInfoData?.firmName || '',
          options: {
            x: '33%',
            y: 5.05,
            h: 0.2,
            w: '33%',
            color: theme.font.base,
            fontFace: 'Open Sans',
            bold: true,
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text: `${firmInfoData?.address?.streetAddress || ''}\n${firmInfoData?.address?.city ? `${firmInfoData?.address?.city},` : ''} ${
            firmInfoData?.address?.state || ''
          } ${firmInfoData?.address?.zipCode || ''}`.trim(),
          options: {
            x: '33%',
            y: 5.2,
            h: 0.3,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text: `${firmInfoData?.website || ''}\n${firmInfoData?.email || ''}`.trim(),
          options: {
            x: '66%',
            y: 5.05,
            h: 0.3,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text:
            firmInfoData?.phone && Object.values(firmInfoData?.phone || {})?.length > 0
              ? `Office: ${formatUserPhoneNumber(firmInfoData?.phone?.countryCode, firmInfoData?.phone?.number)}`.trim()
              : '',
          options: {
            x: '66%',
            y: 5.3,
            h: 0.2,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      }
    ]
  });
};

export const createDisclosureSlideMaster = (
  pptx: PptxGenJS,
  currentDate: string,
  logoSize: { widthInInches: number; heightInInches: number },
  firmInfoData: FirmInfoType,
  templateStylingData: ITemplateStylingSettings,
  theme: DefaultTheme
) => {
  pptx.defineSlideMaster({
    title: 'DISCLOSURE_SLIDE_MASTER',
    background: { color: '#ffffff' },
    objects: [
      // Header
      { rect: { x: 0, y: 0, w: '100%', h: 0.5, fill: { color: theme.layer[2] } } },
      {
        text: {
          text: 'Private Capital Summary and Timeline',
          options: {
            x: 0.5,
            y: 0,
            w: 5.5,
            h: 0.5,
            color: theme.font.strong,
            fontFace: 'Open Sans',
            bold: true,
            fontSize: 12.5
          }
        }
      },
      {
        text: {
          text: `Updated ${currentDate}`,
          options: {
            x: 8,
            y: 0,
            w: 5.5,
            h: 0.5,
            color: theme.font.base,
            fontFace: 'Open Sans',
            italic: true,
            fontSize: 10
          }
        }
      },
      { rect: { x: 0, y: 0.52, w: '100%', h: 0.3, fill: { color: theme.layer[2] } } },
      {
        text: {
          text: 'Disclosure',
          options: {
            x: 0.5,
            y: 0.52,
            w: 5.5,
            h: 0.3,
            color: theme.font.base,
            fontFace: 'Open Sans',
            fontSize: 8.5
          }
        }
      },

      // Footer
      { rect: { x: 0.5, y: 4.95, w: 9, h: 0.01, fill: { color: theme.border.base } } },
      {
        image: {
          path: templateStylingData?.logo,
          x: 0.5,
          y: 5.05,
          w: logoSize.widthInInches,
          h: logoSize.heightInInches
        }
      },
      {
        text: {
          text: firmInfoData?.firmName || '',
          options: {
            x: '33%',
            y: 5.05,
            h: 0.2,
            w: '33%',
            color: theme.font.base,
            fontFace: 'Open Sans',
            bold: true,
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text: `${firmInfoData?.address?.streetAddress || ''}\n${firmInfoData?.address?.city ? `${firmInfoData?.address?.city},` : ''} ${
            firmInfoData?.address?.state || ''
          } ${firmInfoData?.address?.zipCode || ''}`.trim(),
          options: {
            x: '33%',
            y: 5.2,
            h: 0.3,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text: `${firmInfoData?.website || ''}\n${firmInfoData?.email || ''}`.trim(),
          options: {
            x: '66%',
            y: 5.05,
            h: 0.3,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      },
      {
        text: {
          text:
            firmInfoData?.phone && Object.values(firmInfoData?.phone || {})?.length > 0
              ? `Office: ${formatUserPhoneNumber(firmInfoData?.phone?.countryCode, firmInfoData?.phone?.number)}`.trim()
              : '',
          options: {
            x: '66%',
            y: 5.3,
            h: 0.2,
            w: '33%',
            color: theme.font.weak,
            fontFace: 'Open Sans',
            fontSize: 7,
            align: 'center'
          }
        }
      }
    ]
  });
};

export const createTitleSlide = (
  pptx: PptxGenJS,
  currentDate: string,
  slideTitle: string,
  logoSize: { widthInInches: number; heightInInches: number },
  firmInfoData: FirmInfoType,
  templateStylingData: ITemplateStylingSettings,
  theme: DefaultTheme
) => {
  const { headerFontStyle } = templateStylingData || {};
  const slide = pptx.addSlide();

  slide.addImage({
    path: templateStylingData?.logo,
    x: 7.4,
    y: 0.4,
    w: logoSize.widthInInches,
    h: logoSize.heightInInches
  });

  slide.addText(slideTitle, {
    x: 0.5,
    y: 2.35,
    h: 0.4,
    fontSize: 24,
    fontFace: headerFontStyle?.fontFamily || 'Roboto Slab',
    color: headerFontStyle?.fontColor || theme.font.strong
  });

  slide.addShape(pptx.ShapeType.line, {
    x: 0.5,
    y: 2.8,
    w: 9,
    h: 0,
    line: { width: 2, color: theme.layer[2] }
  });

  slide.addText(`Private Capital Summary\n${currentDate}`, {
    x: 0.5,
    y: 2.9,
    h: 0.6,
    fontSize: 14,
    fontFace: 'Open Sans',
    color: theme.font.weak
  });

  slide.addText(`Prepared By`, {
    x: 0,
    y: 4.4,
    w: '100%',
    h: 0.4,
    bold: true,
    fontSize: 7,
    lineSpacing: 13,
    fontFace: 'Open Sans',
    color: theme.font.weak,
    align: 'center'
  });

  slide.addText(`${firmInfoData?.firmName}`, {
    x: 0,
    y: 4.57,
    w: '100%',
    h: 0.4,
    bold: true,
    fontSize: 7,
    lineSpacing: 13,
    fontFace: 'Open Sans',
    color: theme.font.base,
    align: 'center'
  });

  slide.addText(getContactInfoString(firmInfoData), {
    x: 0,
    y: 4.8,
    w: '100%',
    h: 0.6,
    fontSize: 7,
    fontFace: 'Open Sans',
    color: theme.font.weak,
    align: 'center'
  });
};

const createTableHeaders = (
  publishedUpdatesTableColumns: TableColumns[],
  accentColor: string,
  theme: DefaultTheme,
  getColumnWidth: (index: number) => number
) => {
  return publishedUpdatesTableColumns.map((column, columnIndex) => ({
    text: column.title.toUpperCase(),
    options: {
      fill: { color: accentColor || '#5A6771' },
      color: theme.context.light,
      fontSize: 5,
      fontFace: 'Open Sans',
      align: 'center' as const,
      valign: 'middle' as const,
      border: { type: 'solid' as const, color: theme.border.base, pt: 1 },
      h: 0.18,
      w: getColumnWidth(columnIndex)
    }
  }));
};

const getPptxTableCellValue = (type: string, value: string, commentary: string): string => {
  if (!value && !commentary) {
    return '--';
  }

  switch (type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      return value || '--';
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return value || '--';
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      if (value && commentary) {
        return `${value} | ${commentary}`;
      }
      return value || commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      if (value && commentary) {
        return `${currencyFormatter.format(+value)} | ${commentary}`;
      }
      return value ? currencyFormatter.format(+value) : commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      if (value && commentary) {
        return `${value ? decimalToPercentage(value) : ''} | ${commentary}`;
      }
      return value ? decimalToPercentage(value) : commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      if (value && commentary) {
        return `${value ? dateFormatter(format(new Date(value), 'yyyy-MM-dd'), 'yyyy-MM-dd') : ''} | ${commentary}`;
      }
      return value ? dateFormatter(format(new Date(value), 'yyyy-MM-dd'), 'yyyy-MM-dd') : commentary;
    default:
      return '--';
  }
};

const getSentimentFlagStyles = (sentimentFlag: DocumentRecordSentimentFlag) => {
  const sentimentFlagStyles: Record<DocumentRecordSentimentFlag, { color: string; highlight: string }> = {
    Positive: { color: '#6aa94f', highlight: '#f0fcec' },
    Negative: { color: '#cc0100', highlight: '#ffebec' },
    Neutral: { color: '#474747', highlight: '#f3f3f3' }
  };

  return sentimentFlagStyles[sentimentFlag] || { color: '#474747', highlight: '#f3f3f3' };
};

const createTableRows = (
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[],
  source: PublishedInvestmentManagerUpdatesSourceEnum,
  theme: DefaultTheme,
  t: TFunction,
  tableColumnsLength: number,
  getColumnWidth: (index: number) => number
) => {
  return publishedUpdates.map((row) => {
    const { publishedUpdates, legalEntityCommittedCapital, investment, lastUpdate, sentimentFlag, sentimentFlagLabel } = row || {};

    const dynamicColumns = publishedUpdates?.map((update, updateIndex) => ({
      text: getPptxTableCellValue(update?.documentField?.type, update?.documentFieldAnswer?.value, update?.documentFieldAnswer?.commentary),
      options: {
        fontSize: 6,
        color: theme.font.base,
        fontFace: 'Open Sans',
        align: 'left' as const,
        w: getColumnWidth(updateIndex + 1)
      }
    }));

    const legalEntityCommittedCapitalTextRuns =
      legalEntityCommittedCapital?.flatMap((legalEntityCommittedCapitalItem, index) => {
        const { entityName, committedCapital } = legalEntityCommittedCapitalItem || {};

        return [
          {
            text: entityName || '',
            options: {
              color: theme.font.base
            }
          },
          {
            text: `: ${currencyFormatter.format(committedCapital)}`,
            options: {
              color: theme.font.base,
              bold: true
            }
          },
          ...(index < (legalEntityCommittedCapital?.length || 0) - 1
            ? [
                {
                  text: '\n ',
                  options: { fontSize: 2 }
                }
              ]
            : [])
        ];
      }) || [];

    const assetClassValue = getPublishedUpdatesTableAssetClassValue(investment);
    const pacingValue = getPublishedUpdatesTablePacingValue(investment, t);
    const lastPublishedUpdateText = !!lastUpdate ? dateFormatter(format(new Date(lastUpdate), 'yyyy-MM-dd'), 'yyyy-MM-dd') : '--';

    const strategyCellTextRuns = [
      {
        text: `${assetClassValue} (${pacingValue})`,
        options: {
          color: theme.font.weak
        }
      },
      {
        text: '\n ',
        options: { fontSize: 3 }
      },
      {
        text: investment?.name || '',
        options: {
          color: theme.font.base,
          bold: true
        }
      },
      ...(source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB && legalEntityCommittedCapital?.length > 0
        ? [
            {
              text: '\n ',
              options: { fontSize: 3 }
            },
            ...legalEntityCommittedCapitalTextRuns
          ]
        : [])
    ];

    return [
      {
        text: strategyCellTextRuns,
        options: {
          fontSize: 6,
          fontFace: 'Open Sans',
          align: 'left' as const,
          border: { type: 'solid' as const, color: theme.border.base, pt: 1 },
          w: getColumnWidth(0)
        }
      },
      ...dynamicColumns,
      {
        text: [
          {
            text: lastPublishedUpdateText
          },
          ...(sentimentFlag && sentimentFlagLabel
            ? [
                {
                  text: '\n ',
                  options: { fontSize: 3 }
                },
                {
                  text: sentimentFlagLabel,
                  options: {
                    color: getSentimentFlagStyles(sentimentFlag).color,
                    highlight: getSentimentFlagStyles(sentimentFlag).highlight
                  }
                }
              ]
            : [])
        ],
        options: {
          fontSize: 6,
          fontFace: 'Open Sans',
          align: 'center' as const,
          border: { type: 'solid' as const, color: theme.border.base, pt: 1 },
          w: getColumnWidth(tableColumnsLength - 1)
        }
      }
    ];
  });
};

export const createTableSlide = (
  pptx: PptxGenJS,
  publishedUpdatesTableColumns: TableColumns[],
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[],
  templateStylingData: ITemplateStylingSettings,
  theme: DefaultTheme,
  source: PublishedInvestmentManagerUpdatesSourceEnum,
  t: TFunction<'enums', undefined, 'enums'>
) => {
  const { accentColor } = templateStylingData || {};
  const tableColumnsLength = publishedUpdatesTableColumns.length;
  const tableWidth = 9; // Total table width in inches

  const firstTwoColumnsWidth = 15;
  const remainingColumnsCount = Math.max(1, tableColumnsLength - 2);
  const remainingColumnsWidth = (100 - firstTwoColumnsWidth * 2) / remainingColumnsCount;

  const equalColumnWidth = remainingColumnsWidth > firstTwoColumnsWidth ? 100 / tableColumnsLength : null;

  const getColumnWidth = (index: number): number => {
    let widthPercent: number;
    if (equalColumnWidth !== null) {
      widthPercent = equalColumnWidth;
    } else if (index < 2) {
      widthPercent = firstTwoColumnsWidth;
    } else {
      widthPercent = remainingColumnsWidth;
    }
    return (tableWidth * widthPercent) / 100;
  };

  const tableHeaders = createTableHeaders(publishedUpdatesTableColumns, accentColor, theme, getColumnWidth);
  const tableRows = createTableRows(publishedUpdates, source, theme, t, tableColumnsLength, getColumnWidth);

  const colW = Array.from({ length: tableColumnsLength }, (_, index) => getColumnWidth(index));

  if (!tableRows.length) {
    const slide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
    slide.addTable([tableHeaders], {
      x: 0.5,
      y: 0.7,
      w: 9,
      h: 3,
      rowH: 0.18,
      colW,
      color: '#000000',
      border: { type: 'solid', color: theme.border.base, pt: 1 }
    });
    return;
  }

  const slide = pptx.addSlide({ masterName: 'SLIDE_MASTER' });
  slide.addTable([tableHeaders, ...tableRows], {
    x: 0.5,
    y: 0.7,
    w: tableWidth,
    h: 4,
    rowH: [0.18],
    colW,
    autoPage: true,
    autoPageLineWeight: 0.5,
    newSlideStartY: 0.7,
    autoPageRepeatHeader: true,
    color: '#000000',
    border: { type: 'solid', color: theme.border.base, pt: 1 }
  });
};

export const createDisclosurePreviewSlide = (
  pptx: PptxGenJS,
  logoSize: { widthInInches: number; heightInInches: number },
  templateStylingData: ITemplateStylingSettings,
  theme: DefaultTheme
) => {
  const { headerFontStyle } = templateStylingData || {};
  const slide = pptx.addSlide();

  slide.addImage({
    path: templateStylingData?.logo,
    x: 7.4,
    y: 0.4,
    w: logoSize.widthInInches,
    h: logoSize.heightInInches
  });

  slide.addText('Disclosure', {
    x: 0.5,
    y: 2.35,
    h: 0.4,
    fontSize: 24,
    fontFace: headerFontStyle?.fontFamily || 'Roboto Slab',
    color: headerFontStyle?.fontColor || theme.font.strong
  });

  slide.addShape(pptx.ShapeType.line, {
    x: 0.5,
    y: 2.8,
    w: 9,
    h: 0,
    line: { width: 2, color: theme.layer[2] }
  });
};

export const createDisclosureSlide = (pptx: PptxGenJS, theme: DefaultTheme) => {
  const slide = pptx.addSlide({ masterName: 'DISCLOSURE_SLIDE_MASTER' });

  const textTable = [
    [
      {
        text: UPDATES_SUMMARY_MOCKED_DISCLOSURE_TEXT,
        options: { fontSize: 6, fontFace: 'Open Sans', color: theme.font.weak }
      }
    ]
  ];

  slide.addTable(textTable, {
    x: 0.5,
    y: 1,
    w: '90%',
    h: 6,
    autoPage: true,
    autoPageLineWeight: 0.5,
    newSlideStartY: 1
  });
};
